/* Compact sidebar card with daily counters for completed, feed additions, and queue additions. */

import { useMemo, useState, useEffect, type ReactNode } from "react";
import { useQuery } from "convex/react";
import { CheckCircle2, Inbox, ListVideo } from "lucide-react";
import { api } from "../../../convex/_generated/api";
import { getLocalTodayStart } from "@/lib/getLocalTodayStart";

interface StatRowProps {
  icon: ReactNode;
  label: string;
  value: number | undefined;
}

function StatRow({ icon, label, value }: StatRowProps) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="inline-flex items-center gap-1.5 text-[11px] text-muted-foreground">
        {icon}
        {label}
      </span>
      <span className="text-xs font-semibold tabular-nums">{value ?? "..."}</span>
    </div>
  );
}

function useDateKey(): string {
  const [dateKey, setDateKey] = useState(() => new Date().toDateString());

  useEffect(() => {
    const now = new Date();
    const tomorrow = new Date(now);
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(0, 0, 0, 0);
    const msUntilMidnight = tomorrow.getTime() - now.getTime();

    const timer = setTimeout(() => {
      setDateKey(new Date().toDateString());
    }, msUntilMidnight + 100);

    return () => clearTimeout(timer);
  }, [dateKey]);

  return dateKey;
}

export function SidebarTodayStatsCard() {
  const dateKey = useDateKey();
  const todayStart = useMemo(() => getLocalTodayStart(), [dateKey]);
  const todayStats = useQuery(api.feedItems.getTodayStats, { todayStart });
  const queuedToday = useQuery(api.feedItems.countQueuedToday, { todayStart });
  const recentHistory = useQuery(api.feedUpdateHistory.getRecentSummary, { limit: 50 });

  const addedToFeedToday = useMemo(() => {
    if (!recentHistory) return undefined;
    return recentHistory.reduce((sum, entry) => {
      if (entry.completedAt < todayStart) return sum;
      return sum + entry.newItemsCount;
    }, 0);
  }, [recentHistory, todayStart]);

  return (
    <div className="px-2 space-y-1">
      <p className="text-xs font-medium text-muted-foreground mb-1.5">Today</p>
      <StatRow
        icon={<CheckCircle2 className="size-3 text-muted-foreground/70" />}
        label="Completed"
        value={todayStats?.itemsCompleted}
      />
      <StatRow
        icon={<Inbox className="size-3 text-muted-foreground/70" />}
        label="Added to feed"
        value={addedToFeedToday}
      />
      <StatRow
        icon={<ListVideo className="size-3 text-muted-foreground/70" />}
        label="Added to queue"
        value={queuedToday}
      />
    </div>
  );
}
